import { isJsonObject } from "@/types/json";
import { isNumberValue } from "@/utils/typePredicates";

/**
 * Minimum-supported-build policy for the iOS shell (SD-19983) — where the
 * policy lives, what counts as a valid one, and how it compares against the
 * installed native build.
 *
 * The policy is a static document served by the DEPLOYED web app
 * (`public/ios-minimum-build.json`), never the copy bundled inside the shell.
 * A store build ships its own frozen `dist/`, so the bundled file could only
 * ever repeat what was true at archive time; reading the deployed one is what
 * lets an ordinary web release raise or lower the threshold for apps already
 * installed.
 *
 * Every failure mode in this module resolves to "no update required". A build
 * that cannot read or cannot understand the policy has to keep working: a gate
 * that locks people out on a typo, a 404, or an offline launch is worse than no
 * gate at all. A wrongly raised minimum is recoverable — lower it and deploy,
 * and `updateGate.ts` releases running apps on their next foreground — but a
 * block nobody intended should never happen in the first place.
 */

/** A validated policy document. `minimumBuild: 0` keeps the gate off. */
export interface MinimumBuildPolicy {
  /** Lowest native build allowed to run; anything below it must update. */
  minimumBuild: number;
}

const PRODUCTION_POLICY_URL = "https://app.sameday.ai/ios-minimum-build.json";

/**
 * Where to read the policy from.
 *
 * Defaults to the production web origin because App Store builds are always
 * archived against prod env (MOBILE.md). The override exists so a TestFlight
 * build can be pointed at a PR preview's own copy
 * (`https://app-stg.sameday.ai/pr-<N>/ios-minimum-build.json`) and rehearse the
 * whole gate without touching the production policy.
 */
export const IOS_MINIMUM_BUILD_POLICY_URL =
  import.meta.env.VITE_IOS_MINIMUM_BUILD_POLICY_URL?.trim() ||
  PRODUCTION_POLICY_URL;

/**
 * Validate a fetched policy document.
 *
 * Returns null for anything that is not a non-negative safe-integer
 * `minimumBuild`. Unknown members are ignored so the document can grow, but
 * that growth is one-way: a build shipped today reads `minimumBuild` and
 * nothing else, so a later member may only ever RELAX the requirement. Scoping
 * one (a rollout percentage, a build allowlist) would be ignored by every app
 * already in the field, which is the entire population this policy exists to
 * reach. Narrowing needs a version the client must recognize, not a new key.
 */
export const parseMinimumBuildPolicy = (
  body: unknown,
): MinimumBuildPolicy | null => {
  if (!isJsonObject(body)) return null;
  const { minimumBuild } = body;
  if (!isNumberValue(minimumBuild)) return null;
  if (!Number.isSafeInteger(minimumBuild) || minimumBuild < 0) return null;
  return { minimumBuild };
};

/**
 * Parse `App.getInfo().build` — the iOS CFBundleVersion — into a comparable
 * number.
 *
 * This repo's `CURRENT_PROJECT_VERSION` is a plain incrementing integer (37 as
 * of writing), and that is the only shape compared here. CFBundleVersion also
 * permits dotted forms such as "1.2.3"; rather than invent an ordering for one,
 * the gate declines to compare and stays off.
 */
export const parseNativeBuildNumber = (build: string): number | null => {
  const trimmed = build.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const parsed = Number(trimmed);
  return Number.isSafeInteger(parsed) ? parsed : null;
};

/**
 * True only when a validated policy puts this installed build below the
 * minimum. An unknown build or an unknown policy is never an upgrade
 * requirement, and a build equal to the minimum is still supported.
 */
export const requiresUpdate = (
  installedBuild: number | null,
  policy: MinimumBuildPolicy | null,
): boolean =>
  installedBuild !== null &&
  policy !== null &&
  installedBuild < policy.minimumBuild;
